import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { Button } from '../ui/Button';
import { ToastManager } from '../ui/ToastManager';
import { GameState } from '../systems/GameState';
import { AudioManager } from '../systems/AudioManager';
import { Theme, drawCard, staggerFadeIn } from '../ui/theme';
import type { MiniGameId } from '../types';

type MiniDef = { id: MiniGameId | 'placeholder'; emoji: string; name: string; desc: string; sceneKey?: string };

const MINIS: MiniDef[] = [
  { id: 'rhythm',       emoji: '🎵', name: '리듬게임',   desc: '비트에 맞춰 탭!',   sceneKey: 'RhythmGameScene' },
  { id: 'colorMatch',   emoji: '🎨', name: '컬러매치',   desc: '색깔을 맞춰요',      sceneKey: 'ColorMatchScene' },
  { id: 'dressUp',      emoji: '👗', name: '드레스업',   desc: '테마에 맞게 코디',   sceneKey: 'DressUpChallengeScene' },
  { id: 'memory',       emoji: '👀', name: '메모리',     desc: '옷을 기억해요',      sceneKey: 'MemoryScene' },
  { id: 'shoppingRush', emoji: '🧱', name: '벽돌깨기',  desc: '공으로 벽돌 깨기!',   sceneKey: 'ShoppingRushScene' },
  { id: 'walkingTiming',emoji: '💃', name: '워킹',       desc: '비트에 맞춰 워킹',   sceneKey: 'WalkingTimingScene' },
  { id: 'outfitPuzzle', emoji: '🧩', name: '코디퍼즐',   desc: '코디를 맞춰봐요',    sceneKey: 'OutfitPuzzleScene' },
  { id: 'math',         emoji: '🔢', name: '수학챌린지', desc: '수학 문제 도전',      sceneKey: 'MathChallengeScene' },
];

// Card layout — 4×2 grid, centered on 1440px
const CARD_W = 220;
const CARD_H = 220;
const H_GAP = 28;
const V_GAP = 28;
const COLS = 4;

const TOTAL_GRID_W = COLS * CARD_W + (COLS - 1) * H_GAP;   // 220*4 + 3*28 = 964
const GRID_START_X = (GAME_WIDTH - TOTAL_GRID_W) / 2;       // 238

const ROW1_Y = 215;
const ROW2_Y = ROW1_Y + CARD_H + V_GAP;                     // 463

export class MiniGameHubScene extends Phaser.Scene {
  private toast!: ToastManager;

  constructor() { super('MiniGameHubScene'); }

  create(): void {
    AudioManager.setScene(this);
    AudioManager.playBgm('minigame');
    this.scene.launch('HUDScene');
    this.cameras.main.setBackgroundColor('#fef0f5');
    if (this.textures.exists('bg_minigame')) {
      const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_minigame');
      // 화면을 채우도록 스케일 — cover 방식
      const sx = GAME_WIDTH / bg.width;
      const sy = GAME_HEIGHT / bg.height;
      const s = Math.max(sx, sy);
      bg.setScale(s).setDepth(-100);
      // 가독성용 살짝 톤 다운
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xfef0f5, 0.55).setDepth(-99);
    }
    this.cameras.main.fadeIn(200);
    this.toast = new ToastManager(this);

    // ── Header ────────────────────────────────────────────────────────────
    this.add.text(GAME_WIDTH / 2, 75, '🎮 미니게임', {
      fontFamily: Theme.font.family,
      fontSize: '34px',
      fontStyle: '800',
      color: Theme.hex.text,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 125, '게임을 하면 별과 코인을 획득해요! 8개의 미니게임 도전!', {
      fontFamily: Theme.font.family,
      fontSize: '15px',
      color: Theme.hex.textMid,
    }).setOrigin(0.5);

    // Decorative corner sparkles
    this.add.text(40, 40, '✨', { fontSize: '22px', alpha: 0.4 } as Phaser.Types.GameObjects.Text.TextStyle).setAlpha(0.4);
    this.add.text(GAME_WIDTH - 60, 40, '✨', { fontSize: '22px' } as Phaser.Types.GameObjects.Text.TextStyle).setAlpha(0.4);

    // Featured game index (first = rhythm, gets gold highlight)
    const featuredIdx = 0;

    const cardObjects: Phaser.GameObjects.GameObject[] = [];

    MINIS.forEach((m, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = GRID_START_X + col * (CARD_W + H_GAP) + CARD_W / 2;
      const cy = (row === 0 ? ROW1_Y : ROW2_Y) + CARD_H / 2;

      const isFeatured = i === featuredIdx;
      const isMath = m.id === 'math';

      const fillColor = isFeatured ? Theme.colors.gold.light
        : isMath ? Theme.colors.purple.light
        : 0xffffff;
      const borderColor = isFeatured ? Theme.colors.gold.main
        : isMath ? Theme.colors.purple.main
        : Theme.colors.pink.light;
      const borderWidth = isFeatured ? 3 : 2;

      // Card container for easy hover scaling
      const cardContainer = this.add.container(cx, cy);

      // Card background via drawCard (uses Graphics)
      const g = this.add.graphics();
      drawCard(g, -CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, {
        fill: fillColor,
        border: borderColor,
        borderWidth,
        radius: Theme.radius.card,
      });
      cardContainer.add(g);

      // Featured badge — inside top of card
      if (isFeatured) {
        const badgeG = this.add.graphics();
        badgeG.fillStyle(Theme.colors.gold.main, 1);
        badgeG.fillRoundedRect(-44, -CARD_H / 2 + 10, 88, 24, 8);
        cardContainer.add(badgeG);
        const badgeText = this.add.text(0, -CARD_H / 2 + 22, '⭐ 추천', {
          fontFamily: Theme.font.family,
          fontSize: '11px',
          fontStyle: '900',
          color: '#6b4a00',
        }).setOrigin(0.5);
        cardContainer.add(badgeText);
        // Pulse glow on featured
        this.tweens.add({ targets: cardContainer, alpha: 0.88, duration: 1000, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
      }

      // Emoji — large, centered
      const emojiText = this.add.text(0, -38, m.emoji, { fontSize: '58px' }).setOrigin(0.5);
      cardContainer.add(emojiText);

      // Name
      const nameText = this.add.text(0, 46, m.name, {
        fontFamily: Theme.font.family,
        fontSize: '17px',
        fontStyle: '800',
        color: Theme.hex.text,
      }).setOrigin(0.5);
      cardContainer.add(nameText);

      // Description
      const descText = this.add.text(0, 72, m.desc, {
        fontFamily: Theme.font.family,
        fontSize: '12px',
        color: Theme.hex.textMid,
      }).setOrigin(0.5);
      cardContainer.add(descText);

      // Best score — bottom of card
      const best = GameState.data.stats.minigameBest[m.id as MiniGameId] ?? 0;
      const bestText = this.add.text(0, 95, `최고 ⭐ ${best}`, {
        fontFamily: Theme.font.family,
        fontSize: '12px',
        fontStyle: '700',
        color: best > 0 ? Theme.hex.gold : Theme.hex.textLight,
      }).setOrigin(0.5);
      cardContainer.add(bestText);

      // Invisible hit zone
      const hit = this.add.rectangle(0, 0, CARD_W, CARD_H, 0x000000, 0);
      hit.setInteractive({ useHandCursor: true });

      hit.on('pointerover', () => {
        AudioManager.playSfx('click');
        this.tweens.add({
          targets: cardContainer,
          scaleX: 1.05,
          scaleY: 1.05,
          duration: 130,
          ease: 'Back.Out',
        });
      });
      hit.on('pointerout', () => {
        this.tweens.add({
          targets: cardContainer,
          scaleX: 1,
          scaleY: 1,
          duration: 120,
          ease: 'Back.Out',
        });
        // Restore alpha for featured
        if (isFeatured) cardContainer.setAlpha(1);
      });
      hit.on('pointerdown', () => {
        AudioManager.playSfx('click');
        if (m.sceneKey) {
          this.cameras.main.fadeOut(200);
          this.cameras.main.once('camerafadeoutcomplete', () => {
            this.scene.stop('HUDScene');
            this.scene.start(m.sceneKey!);
          });
        } else {
          this.toast.show(`${m.emoji} ${m.name}는 다음 단계에서 구현돼요!`);
        }
      });
      cardContainer.add(hit);

      cardObjects.push(cardContainer);
    });

    // Stagger entrance animation
    staggerFadeIn(this, cardObjects, 80, 60);

    // Home button — bottom center
    new Button(this, GAME_WIDTH / 2, GAME_HEIGHT - 50, '← 홈으로', () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HUDScene');
        this.scene.start('HomeScene');
      });
    }, { width: 180, height: 52, style: 'secondary' });
  }
}
